import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/shared/components/ui/dialog';
import { Label } from '@/shared/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import { toast } from 'sonner';
import {
  BookOpen, Plus, RefreshCw, Search, ChevronRight, ChevronDown, Edit,
  Building2, CreditCard, Landmark, Wallet } from
'lucide-react';

const API_URL = API_BASE;
const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('ezerpToken')}`
});

const accountTypeColors = {
  asset: 'bg-green-500/20 text-green-400',
  liability: 'bg-red-500/20 text-red-400',
  equity: 'bg-purple-500/20 text-purple-400',
  revenue: 'bg-blue-500/20 text-blue-400',
  expense: 'bg-amber-500/20 text-amber-400'
};

const accountTypeIcons = {
  asset: Wallet,
  liability: CreditCard,
  equity: Building2,
  revenue: Landmark,
  expense: BookOpen
};

export const ChartOfAccounts = () => {const { t } = useTranslation("finance");
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [expandedGroups, setExpandedGroups] = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const [formData, setFormData] = useState({
    code: '', name: '', account_type: 'asset', sub_type: '', description: '',
    normal_balance: 'debit', is_header: false, is_bank_account: false
  });

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/accounting/chart-of-accounts?`;
      if (typeFilter !== 'all') url += `account_type=${typeFilter}&`;
      if (search) url += `search=${encodeURIComponent(search)}&`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch chart of accounts');
      const data = await res.json();
      setAccounts(data.accounts || []);
    } catch (err) {
      toast.error(err.message);
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {fetchAccounts();}, [typeFilter]);
  useEffect(() => {const timer = setTimeout(fetchAccounts, 300);return () => clearTimeout(timer);}, [search]);

  const handleCreate = async () => {
    try {
      const res = await fetch(`${API_URL}/api/accounting/chart-of-accounts`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(formData)
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to create account');
      }
      toast.success('Account created successfully');
      setShowCreate(false);
      setFormData({ code: '', name: '', account_type: 'asset', sub_type: '', description: '', normal_balance: 'debit', is_header: false, is_bank_account: false });
      fetchAccounts();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const toggleGroup = (type) => {
    setExpandedGroups((prev) => ({ ...prev, [type]: !prev[type] }));
  };

  // Group accounts by type
  const groupedAccounts = accounts.reduce((acc, account) => {
    const type = account.account_type || 'other';
    if (!acc[type]) acc[type] = [];
    acc[type].push(account);
    return acc;
  }, {});

  const accountTypes = ['asset', 'liability', 'equity', 'revenue', 'expense'];

  return (
    <div className="space-y-6" data-testid="chart-of-accounts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("chartOfAccounts", "Chart of Accounts")}</h1>
          <p className="text-muted-foreground mt-1">{t("manageYourGeneral", "Manage your general ledger accounts")}</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="gap-2 bg-green-600 hover:bg-green-700">
          <Plus className="h-4 w-4" />{t("addAccount", "Add Account")}
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search accounts..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10" />
              
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Account Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allTypes", "All Types")}</SelectItem>
                <SelectItem value="asset">{t("assets", "Assets")}</SelectItem>
                <SelectItem value="liability">{t("liabilities", "Liabilities")}</SelectItem>
                <SelectItem value="equity">{t("equity", "Equity")}</SelectItem>
                <SelectItem value="revenue">{t("revenue", "Revenue")}</SelectItem>
                <SelectItem value="expense">{t("expenses", "Expenses")}</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchAccounts}><RefreshCw className="h-4 w-4" /></Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-5">
        {accountTypes.map((type) => {
          const count = groupedAccounts[type]?.length || 0;
          const Icon = accountTypeIcons[type];
          return (
            <Card key={type} className="cursor-pointer hover:border-green-500 transition-colors" onClick={() => setTypeFilter(type)}>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground capitalize">{type}</p>
                    <p className="text-2xl font-bold">{count}</p>
                  </div>
                  <div className={`p-2 rounded-lg ${accountTypeColors[type]}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                </div>
              </CardContent>
            </Card>);

        })}
      </div>

      {/* Accounts List */}
      <Card>
        <CardContent className="p-0">
          {loading ?
          <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500" />
            </div> :
          accounts.length === 0 ?
          <div className="text-center py-16">
              <BookOpen className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg text-muted-foreground">{t("noAccountsFound", "No accounts found")}</p>
              <Button onClick={() => setShowCreate(true)} className="mt-4 bg-green-600 hover:bg-green-700">
                <Plus className="h-4 w-4 mr-2" />{t("addFirstAccount", "Add First Account")}
            </Button>
            </div> :

          <div className="divide-y divide-slate-700">
              {accountTypes.map((type) => {
              const typeAccounts = groupedAccounts[type] || [];
              if (typeAccounts.length === 0) return null;
              const isExpanded = expandedGroups[type] !== false;
              const Icon = accountTypeIcons[type];

              return (
                <div key={type}>
                    <div
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-800/30"
                    onClick={() => toggleGroup(type)}>
                    
                      <div className="flex items-center gap-3">
                        {isExpanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                        <div className={`p-2 rounded-lg ${accountTypeColors[type]}`}>
                          <Icon className="h-5 w-5" />
                        </div>
                        <span className="font-semibold capitalize">{type}</span>
                        <Badge variant="secondary">{typeAccounts.length}</Badge>
                      </div>
                    </div>
                    {isExpanded &&
                  <div className="bg-slate-800/20">
                        {typeAccounts.map((account) =>
                    <div key={account.id} className="flex items-center justify-between px-12 py-3 border-t border-slate-700/50 hover:bg-slate-800/30">
                            <div className="flex items-center gap-4">
                              <span className="font-mono text-sm text-muted-foreground w-16">{account.code}</span>
                              <div>
                                <p className="font-medium">{account.name}</p>
                                {account.sub_type &&
                          <p className="text-sm text-muted-foreground">{account.sub_type}</p>
                          }
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              {account.is_bank_account &&
                        <Badge className="bg-blue-500/20 text-blue-400">{t("bank", "Bank")}</Badge>
                        }
                              {account.is_control_account &&
                        <Badge className="bg-purple-500/20 text-purple-400">{t("control", "Control")}</Badge>
                        }
                              {account.is_header &&
                        <Badge className="bg-gray-500/20 text-gray-400">{t("header", "Header")}</Badge>
                        }
                              <span className="text-sm text-muted-foreground capitalize">{account.normal_balance}</span>
                            </div>
                          </div>
                    )}
                      </div>
                  }
                  </div>);

            })}
            </div>
          }
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("addNewAccount", "Add New Account")}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("accountCode", "Account Code *")}</Label>
                <Input value={formData.code} onChange={(e) => setFormData({ ...formData, code: e.target.value })} placeholder="1000" />
              </div>
              <div>
                <Label>{t("accountType", "Account Type *")}</Label>
                <Select value={formData.account_type} onValueChange={(v) => setFormData({ ...formData, account_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="asset">{t("asset", "Asset")}</SelectItem>
                    <SelectItem value="liability">{t("liability", "Liability")}</SelectItem>
                    <SelectItem value="equity">{t("equity", "Equity")}</SelectItem>
                    <SelectItem value="revenue">{t("revenue", "Revenue")}</SelectItem>
                    <SelectItem value="expense">{t("expense", "Expense")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>{t("accountName", "Account Name *")}</Label>
              <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Cash and Cash Equivalents" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("subType", "Sub Type")}</Label>
                <Input value={formData.sub_type} onChange={(e) => setFormData({ ...formData, sub_type: e.target.value })} placeholder="current_asset" />
              </div>
              <div>
                <Label>{t("normalBalance", "Normal Balance")}</Label>
                <Select value={formData.normal_balance} onValueChange={(v) => setFormData({ ...formData, normal_balance: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="debit">{t("debit", "Debit")}</SelectItem>
                    <SelectItem value="credit">{t("credit", "Credit")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>{t("description", "Description")}</Label>
              <Input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Optional description" />
            </div>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_header}
                  onChange={(e) => setFormData({ ...formData, is_header: e.target.checked })}
                  className="w-4 h-4" />
                
                <span className="text-sm">{t("headerAccount", "Header Account")}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_bank_account}
                  onChange={(e) => setFormData({ ...formData, is_bank_account: e.target.checked })}
                  className="w-4 h-4" />
                
                <span className="text-sm">{t("bankAccount", "Bank Account")}</span>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>{t("cancel", "Cancel")}</Button>
            <Button onClick={handleCreate} className="bg-green-600 hover:bg-green-700">{t("createAccount", "Create Account")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>);

};

export default ChartOfAccounts;